import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../../../core/services/api.service';
import { finalize } from 'rxjs/operators';
import { TranslatePipe } from '../../../../../../core/pipes/translate.pipe';

export interface BrokerProfile {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  role: string;
  ShowProfile: string;
  notificationSettings: { email: boolean; app: boolean };
  id: string;
  brokerType: string;
  isTrusted: boolean;
  licenseNumber: string;
  specializations: string;
  licenseDocument: string;
  logo?: string;
  profileImage?: string;
  companyName?: string;
  commercialRegisterNumber?: string;
  aboutMe?: string;
  location?: string;
  website?: string;
  aboutCompany_ar?: string;
  aboutCompany_en?: string;
  commercialRegisterDocument?: string;
  yearsOfExperience: number;
  serviceAreas: string[];
  languages: string[];
  socialLinks: { linkedin?: string; facebook?: string; instagram?: string; twitter?: string };
  totalReviews: number;
  averageRating: number;
  avgResponseTimeMinutes: number | null;
  responseTimeLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EditForm {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  aboutMe: string;
  location: string;
  website: string;
  aboutCompany_ar: string;
  aboutCompany_en: string;
  specializations: string;
  yearsOfExperience: number;
  companyName: string;
  commercialRegisterNumber: string;
  licenseNumber: string;
  ShowProfile: string;
  notificationEmail: boolean;
  notificationApp: boolean;
  serviceAreas: string;
  languages: string;
  socialLinkedin: string;
  socialFacebook: string;
  socialInstagram: string;
  socialTwitter: string;
}

@Component({
  selector: 'app-profile',
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './profile.html',
})
export class BrokerProfile implements OnInit {
  profile: BrokerProfile | null = null;
  isLoading = false;
  error = '';

  // Edit panel state
  showEditPanel = false;
  isSaving = false;
  saveError = '';
  saveSuccess = '';
  editForm: EditForm = this.emptyForm();

  // File selections
  logoFile: File | null = null;
  logoPreview: string | null = null;
  licenseFile: File | null = null;
  licenseFileName = '';
  commercialRegisterFile: File | null = null;
  commercialRegisterFileName = '';

  constructor(private api: ApiService) { }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.error = '';
    this.api
      .get<{ resProfile: BrokerProfile }>('/broker/profile')
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res) => (this.profile = res.resProfile),
        error: (err) => (this.error = err?.message || 'Failed to load profile.'),
      });
  }

  openEdit(): void {
    if (!this.profile) return;
    this.editForm = {
      firstName: this.profile.firstName,
      lastName: this.profile.lastName,
      email: this.profile.email || '',
      phoneNumber: this.profile.phoneNumber || '',
      aboutMe: this.profile.aboutMe || '',
      location: this.profile.location || '',
      website: this.profile.website || '',
      aboutCompany_ar: this.profile.aboutCompany_ar || '',
      aboutCompany_en: this.profile.aboutCompany_en || '',
      specializations: this.profile.specializations || '',
      yearsOfExperience: this.profile.yearsOfExperience || 0,
      companyName: this.profile.companyName || '',
      commercialRegisterNumber: this.profile.commercialRegisterNumber || '',
      licenseNumber: this.profile.licenseNumber || '',
      ShowProfile: this.profile.ShowProfile,
      notificationEmail: this.profile.notificationSettings?.email ?? false,
      notificationApp: this.profile.notificationSettings?.app ?? false,
      serviceAreas: (this.profile.serviceAreas || []).join(', '),
      languages: (this.profile.languages || []).join(', '),
      socialLinkedin: this.profile.socialLinks?.linkedin || '',
      socialFacebook: this.profile.socialLinks?.facebook || '',
      socialInstagram: this.profile.socialLinks?.instagram || '',
      socialTwitter: this.profile.socialLinks?.twitter || '',
    };
    this.logoFile = null;
    this.logoPreview = null;
    this.licenseFile = null;
    this.licenseFileName = '';
    this.commercialRegisterFile = null;
    this.commercialRegisterFileName = '';
    this.saveError = '';
    this.saveSuccess = '';
    this.showEditPanel = true;
  }

  closeEdit(): void {
    this.showEditPanel = false;
  }

  onLogoChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.logoFile = file;
    const reader = new FileReader();
    reader.onload = (e) => (this.logoPreview = e.target?.result as string);
    reader.readAsDataURL(file);
  }

  onLicenseChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.licenseFile = file;
    this.licenseFileName = file.name;
  }

  onCommercialRegisterChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.commercialRegisterFile = file;
    this.commercialRegisterFileName = file.name;
  }

  saveProfile(): void {
    this.isSaving = true;
    this.saveError = '';
    this.saveSuccess = '';
    const isCompanyBroker = this.profile?.brokerType === 'company';

    const fd = new FormData();
    if (this.editForm.firstName) fd.append('firstName', this.editForm.firstName);
    if (this.editForm.lastName) fd.append('lastName', this.editForm.lastName);
    if (this.editForm.phoneNumber) fd.append('phoneNumber', this.editForm.phoneNumber);
    if (this.editForm.aboutMe) fd.append('aboutMe', this.editForm.aboutMe);
    if (this.editForm.ShowProfile) fd.append('showProfile', this.editForm.ShowProfile);
    if (this.editForm.location) fd.append('location', this.editForm.location);
    
    fd.append('notificationSettings[email]', String(this.editForm.notificationEmail));
    fd.append('notificationSettings[app]', String(this.editForm.notificationApp));

    if (this.editForm.yearsOfExperience) fd.append('yearsOfExperience', String(this.editForm.yearsOfExperience));
    if (this.editForm.licenseNumber) fd.append('licenseNumber', this.editForm.licenseNumber);
    if (this.editForm.specializations) fd.append('specialization', this.editForm.specializations);

    if (isCompanyBroker) {
      if (this.editForm.companyName) fd.append('companyName', this.editForm.companyName);
      if (this.editForm.commercialRegisterNumber) fd.append('commercialRegisterNumber', this.editForm.commercialRegisterNumber);
      if (this.editForm.website) fd.append('website', this.editForm.website);
      if (this.editForm.aboutCompany_ar) fd.append('aboutCompany_ar', this.editForm.aboutCompany_ar);
      if (this.editForm.aboutCompany_en) fd.append('aboutCompany_en', this.editForm.aboutCompany_en);
    }

    // Service areas & languages as comma-separated → split to array
    const areas = this.editForm.serviceAreas
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    areas.forEach((a) => fd.append('serviceAreas[]', a));

    const langs = this.editForm.languages
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    langs.forEach((l) => fd.append('languages[]', l));

    if (this.editForm.socialLinkedin)
      fd.append('socialLinks[linkedin]', this.editForm.socialLinkedin);
    if (this.editForm.socialFacebook)
      fd.append('socialLinks[facebook]', this.editForm.socialFacebook);
    if (this.editForm.socialInstagram)
      fd.append('socialLinks[instagram]', this.editForm.socialInstagram);
    if (this.editForm.socialTwitter) fd.append('socialLinks[twitter]', this.editForm.socialTwitter);

    if (this.logoFile) {
      if (isCompanyBroker) {
        fd.append('logo', this.logoFile);
      } else {
        fd.append('profileImage', this.logoFile);
      }
    }
    if (this.licenseFile) fd.append('licenseDocument', this.licenseFile);
    if (isCompanyBroker && this.commercialRegisterFile) fd.append('commercialRegisterDocument', this.commercialRegisterFile);

    this.api
      .patchFormData<{ message: string }>('/broker', fd)
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.saveSuccess = 'Profile updated successfully.';
          this.showEditPanel = false;
          this.load();
        },
        error: (err) => (this.saveError = err?.message || 'Failed to update profile.'),
      });
  }

  stars(avg: number): number[] {
    return Array.from({ length: 5 }, (_, i) => i + 1);
  }

  formatDate(d: string): string {
    return new Date(d).toLocaleDateString('en-EG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  private emptyForm(): EditForm {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      aboutMe: '',
      location: '',
      website: '',
      aboutCompany_ar: '',
      aboutCompany_en: '',
      specializations: 'commercial',
      yearsOfExperience: 0,
      companyName: '',
      commercialRegisterNumber: '',
      licenseNumber: '',
      ShowProfile: 'public',
      notificationEmail: false,
      notificationApp: false,
      serviceAreas: '',
      languages: '',
      socialLinkedin: '',
      socialFacebook: '',
      socialInstagram: '',
      socialTwitter: '',
    };
  }
}
